import { useMemo } from "react";
import { Difficulty } from "shared/games/Difficulty";
import { Game } from "shared/games/types/Game";
import { Todo } from "shared/todos/Todo";
import {
  CompletedTodoSet,
  selectCompletedTodos,
} from "shared/todos/useTodosReducer";
import useTodosContext from "shared/todos/useTodosContext";

interface UpNext {
  difficulty: Difficulty;

  games: {
    game: Game;
    difficulty: Difficulty;

    completionStatus: {
      countComplete: number;
      total: number;
    };

    // placed on each card so we can scroll to when needed
    elementId: string;
  }[];
}

interface State {
  upNext: UpNext[];

  nextTodo: { game: Game; difficulty: Difficulty } | null;
  nextToDoElementId: string | null; // the id of the element the user should do next
}

const gameOrder = [
  Game.fader_grader,
  Game.can_you_pan,
  Game.is_this_compressor_on,
  Game.freq_finder_boost,
  Game.freq_finder_cut,
  Game.freq_finder_hipass,
  Game.freq_finder_lopass,
];

const difficultyOrder = [Difficulty.easy, Difficulty.medium, Difficulty.hard];

function makeElementId(game: Game, difficulty: Difficulty): string {
  return `${game}-${difficulty}`;
}

function getUpNextByDifficulty({
  difficulty,
  groupedTodos,
  completedTodos,
}: {
  difficulty: Difficulty;
  groupedTodos: GroupedTodos;
  completedTodos: CompletedTodoSet;
}): UpNext {
  return {
    difficulty,
    games: gameOrder.map((game) => {
      const todos =
        groupedTodos[todosGroupKey.get(game, difficulty)]?.todos ?? [];
      const countComplete = todos.reduce(
        (acc, todo) => (completedTodos.has(todo.id) ? acc + 1 : acc),
        0
      );

      return {
        game,
        difficulty,
        elementId: makeElementId(game, difficulty),
        completionStatus: {
          countComplete,
          total: todos.length ?? 0,
        },
      };
    }),
  };
}

/**
 * Group todos by game and difficulty for efficient lookup
 */

const todosGroupKey = {
  get(game: Game, difficulty: Difficulty) {
    return `${game}-${difficulty}`;
  },

  parse(key: string) {
    const [game, difficulty] = key.split("-");

    return {
      game: game as Game,
      difficulty: difficulty as Difficulty,
    };
  },
};

interface GroupedTodos {
  [key: string]: {
    difficulty: Difficulty;
    game: Game;
    todos: Todo[];
  };
}

function groupTodos(todos: Todo[]) {
  const todosByGameAndDifficulty: GroupedTodos = {};

  todos.forEach((todo) => {
    const key = todosGroupKey.get(todo.game, todo.difficulty);

    if (!todosByGameAndDifficulty[key]) {
      todosByGameAndDifficulty[key] = {
        game: todo.game,
        difficulty: todo.difficulty,
        todos: [],
      };
    }

    todosByGameAndDifficulty[key].todos.push(todo);
  });

  return todosByGameAndDifficulty;
}

/**
 *
 */

function findNextTodo(upNext: UpNext[]): State["nextTodo"] {
  for (let i = 0; i < upNext.length; i++) {
    for (let j = 0; j < upNext[i].games.length; j++) {
      const { completionStatus, game, difficulty } = upNext[i].games[j];

      if (completionStatus.countComplete < completionStatus.total) {
        return {
          game,
          difficulty,
        };
      }
    }
  }

  return null;
}

/**
 *
 */

export default function useUpNextState(): State {
  const [state] = useTodosContext();

  return useMemo(() => {
    const groupedTodos = groupTodos(state.todos);
    const upNext = difficultyOrder.map((difficulty) =>
      getUpNextByDifficulty({
        difficulty,
        groupedTodos,
        completedTodos: selectCompletedTodos(state),
      })
    );

    const nextTodo = findNextTodo(upNext);

    return {
      upNext,
      nextTodo,
      nextToDoElementId:
        state.state === "loaded" && nextTodo
          ? todosGroupKey.get(nextTodo.game, nextTodo.difficulty)
          : null,
    };
  }, [state]);
}
